import { createStyles } from '@mantine/core';
import debounce from 'lodash.debounce';
import useTranslation from 'next-translate/useTranslation';
import { useCallback, useMemo, useState } from 'react';

import { HEADER_HEIGHT } from '~/domains/common/components/AppShellHeader/constants';
import { UnstyledButtonLink } from '~/domains/design-system/Button';
import IconSearch from '~/domains/design-system/icons/IconSearch';
import { textStyles } from '~/domains/design-system/Text';
import { TextInput } from '~/domains/design-system/TextInput';
import dt from '~/testing';

import {
  BOARD_SEARCH_FRAME_INSET_PX,
  BOARD_SEARCH_INPUT_ROW_HEIGHT_PX,
  BOARD_SEARCH_PANEL_VERTICAL_PADDING_PX,
  BOARD_SEARCH_ROW_MAX_WIDTH_PX,
} from '../../../constants/boardLayout.const';

const SEARCH_INPUT_ICON_ZONE_PX = 32;

const useStyles = createStyles((theme) => ({
  searchPanel: {
    backgroundColor: theme.colors.gray2[6],
    boxSizing: 'border-box',
    width: '100%',
    height: HEADER_HEIGHT,
  },
  searchPanelFixed: {
    position: 'sticky',
    top: -2,
    zIndex: theme.other.zIndex.header,
  },
  searchPanelContent: {
    paddingTop: BOARD_SEARCH_PANEL_VERTICAL_PADDING_PX,
    paddingBottom: BOARD_SEARCH_PANEL_VERTICAL_PADDING_PX,
    paddingLeft: theme.other.layout.paddingLg,
    paddingRight: theme.other.layout.paddingLg,

    [theme.fn.smallerThan('lg')]: {
      paddingLeft: theme.other.layout.paddingMd,
      paddingRight: theme.other.layout.paddingMd,
    },
    [theme.fn.smallerThan('md')]: {
      paddingLeft: theme.other.layout.paddingSm,
      paddingRight: theme.other.layout.paddingSm,
    },
    [theme.fn.largerThan('lg')]: {
      paddingLeft: BOARD_SEARCH_FRAME_INSET_PX,
      paddingRight: BOARD_SEARCH_FRAME_INSET_PX,
    },
  },
  searchPanelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: /* eslint-disable-line local-rules/use-theme-tokens */ 14,
    maxWidth: BOARD_SEARCH_ROW_MAX_WIDTH_PX,
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    minWidth: 0,
  },
  searchInput: {
    flex: 1,
    minWidth: 0,
    minHeight: BOARD_SEARCH_INPUT_ROW_HEIGHT_PX,
  },
  searchInputWrapper: {
    minHeight: BOARD_SEARCH_INPUT_ROW_HEIGHT_PX,
    height: BOARD_SEARCH_INPUT_ROW_HEIGHT_PX,
    borderRadius: /* eslint-disable-line local-rules/use-theme-tokens */ 10,
  },
  searchInputInput: {
    minHeight: BOARD_SEARCH_INPUT_ROW_HEIGHT_PX,
    height: BOARD_SEARCH_INPUT_ROW_HEIGHT_PX,
    border: 0,
    padding: `${theme.other.spacing._8}px ${theme.other.spacing._8}px ${theme.other.spacing._8}px ${SEARCH_INPUT_ICON_ZONE_PX}px`,
  },
  searchInputIcon: {
    marginTop: 0,
    width: SEARCH_INPUT_ICON_ZONE_PX,
  },
  closeButton: {
    ...textStyles['title-large'],
    flexShrink: 0,
    width: 46,
    textAlign: 'center',
    color: theme.white,
    cursor: 'pointer',
    textDecoration: 'none',

    '@media (hover: hover)': {
      transition: 'opacity 150ms ease-in-out',

      '&:hover': {
        opacity: 0.5,
      },
    },

    [theme.fn.smallerThan('md')]: {
      ...textStyles['title-medium'],
    },
  },
}));

function BoardSearchPanel({
  value,
  onChange,
  closeHref,
  isFixed = false,
}: {
  value: string;
  onChange: (value: string) => void;
  closeHref: string;
  isFixed?: boolean;
}) {
  const { classes, cx } = useStyles();
  const [tempSearchValue, setTempSearchValue] = useState(value);
  const { t } = useTranslation('contest');

  const debouncedOnChange = useMemo(() => debounce(onChange, 500), [onChange]);
  const handleChange = useCallback(
    (newValue: string) => {
      setTempSearchValue(newValue);
      debouncedOnChange(newValue);
    },
    [debouncedOnChange, setTempSearchValue]
  );

  return (
    <div className={cx(classes.searchPanel, { [classes.searchPanelFixed]: isFixed })}>
      <div className={classes.searchPanelContent}>
        <div className={classes.searchPanelRow}>
          <TextInput
            data-test-id={dt.quickPicks.search.inputField}
            placeholder={t('quickPick.board.filters.searchInputPlaceholder')}
            icon={<IconSearch />}
            iconWidth={SEARCH_INPUT_ICON_ZONE_PX}
            value={tempSearchValue}
            classNames={{
              root: classes.searchInput,
              wrapper: classes.searchInputWrapper,
              input: classes.searchInputInput,
              icon: classes.searchInputIcon,
            }}
            onChange={(e) => {
              handleChange(e.target.value);
            }}
            autoComplete="off"
            autoFocus
          />
          <UnstyledButtonLink
            data-test-id={dt.quickPicks.search.boardSearchCloseButton}
            className={classes.closeButton}
            href={closeHref}
          >
            Close
          </UnstyledButtonLink>
        </div>
      </div>
    </div>
  );
}

export default BoardSearchPanel;
